import { get_guid } from '../../visual-analytics/helpers/misc';
import {
  allETLComponentCategories,
  getAllETLComponentTypeManifests,
  getETLComponentType,
} from '../etl-component-types/factory';
import { TpETLObjectTypes } from '../etl-data-types/interfaces';
import {
  TpETLComponentTypeManifest,
  TpETLOutputObject,
  TpETLComponentTypeConfigParamDef,
} from '../etl-component-types/interfaces';
import { TpETLDataContext, TpETLPipeline, TpETLPipelineComponent } from '../interfaces';
import {
  getETLComponentById,
  getETLComponentWithOutputObj,
  getETLComponentTypeInputInfo,
  getETLComponentTypeOutputInfo,
} from '../utils';

import styles from './newETLComponentForm.module.scss';
import { allETLFlows } from '../etl-flows/factory';
import { TpETLFlowManifest } from '../etl-flows/interfaces';
import { getOutputInputCompatibility } from '../etl-data-types/compatibility';
import Tabber from '../../../components/panels/tabber/Tabber';

interface TpCompInputConnection {
  inputId: string;
  sourceCompId: string;
  sourceOutputId: string;
}

interface TpCompExtraParam {
  sourceCompId: string;
  sourceOutputId: string;
}

interface TpComptypeCompatibilityInfo {
  compType: TpETLComponentTypeManifest;
  isCompatible: boolean;
  rejectionReasons: string[]; // only if not compatible
  inputConnections: TpCompInputConnection[]; // only if compatible
  extraParams: TpCompExtraParam[];
}

// Determines if a set of selected input components is compatible with a new component of a specified type
function getCompTypeCompatibilityInfo(
  selectedOutputs: {
    comp: TpETLPipelineComponent;
    outputType: TpETLOutputObject;
  }[],
  compType: TpETLComponentTypeManifest,
): TpComptypeCompatibilityInfo {
  let isCompatible = true;
  const rejectionReasons = [];
  let inputConnections: TpCompInputConnection[] = [];
  const extraParams: TpCompExtraParam[] = [];

  if (compType.inputs.length > 0 && selectedOutputs.length == 0) {
    return {
      compType,
      isCompatible: false,
      inputConnections: [],
      extraParams: [],
      rejectionReasons: [
        `This component needs input(s) of type ${compType.inputs
          .map((inp) => inp.type)
          .join(', ')}. Please select existing component(s) with such output.`,
      ],
    };
  }

  const usedSelectedOutputIds: { [id: string]: boolean } = {};
  const usedOutputs: { [id: string]: boolean } = {};
  for (const input of compType.inputs) {
    // We try to match each input to one of the selected components
    let connection: TpCompInputConnection | null = null;
    let rejectionReason = `Cannot connect ${input.name} to a selected component. Please select an additional component with the output type ${input.type}`;
    for (const { comp: selComp, outputType: output } of selectedOutputs)
      if (!usedOutputs[`${selComp.id}_${output.id}`] && !connection) {
        const connInfo = getOutputInputCompatibility(output, input);
        if (connInfo.isCompatible) {
          connection = {
            inputId: input.id,
            sourceCompId: selComp.id,
            sourceOutputId: output.id,
          };
          usedOutputs[`${selComp.id}_${output.id}`] = true;
        } else {
          rejectionReason = connInfo.rejectionReason;
        }
      }
    if (connection) {
      inputConnections.push(connection);
      usedSelectedOutputIds[`${connection.sourceCompId}_${connection.sourceOutputId}`] = true;
    } else {
      isCompatible = false;
      rejectionReasons.push(rejectionReason);
    }
  }

  if (isCompatible) {
    // we check if there are any selected components left that are not used
    for (const { comp, outputType } of selectedOutputs)
      if (!usedSelectedOutputIds[`${comp.id}_${outputType.id}`]) {
        if (outputType.type !== TpETLObjectTypes.TEXT) {
          // we can only use text as extra params => error
          isCompatible = false;
          // If the component ha
          let rejectionReason: string;
          if (Object.keys(comp.outputObjectIds).length > 1) {
            rejectionReason = `The selected output "${outputType.name}" (component "${comp.name}") cannot be used by this component`;
          } else {
            rejectionReason = `The selected component "${comp.name}" cannot be used by this component`;
          }
          rejectionReasons.push(rejectionReason);
        } else {
          // other object types (currently only text) can be used as extra parameters
          extraParams.push({
            sourceCompId: comp.id,
            sourceOutputId: outputType.id,
          });
        }
      }
  }

  if (rejectionReasons.length > 0) inputConnections = [];

  return {
    compType,
    isCompatible,
    rejectionReasons,
    inputConnections,
    extraParams,
  };
}

function getCompTypeAvailabilityInfo(
  compType: TpETLComponentTypeManifest,
  assetETLDataContext: TpETLDataContext | null,
) {
  if (!compType.getDataAvailablility) return { available: true, message: '' };
  return compType.getDataAvailablility(assetETLDataContext);
}

function getETLComponentOutputObjectId(pipeline: TpETLPipeline, compId: string, outputId: string): string {
  const comp = getETLComponentById(pipeline, compId);
  return comp.outputObjectIds[outputId];
}

function defaultCompParams(params: TpETLComponentTypeConfigParamDef[]): { [id: string]: any } {
  return params
    .filter((param: TpETLComponentTypeConfigParamDef) => param.paramType.defaultValue !== undefined)
    .reduce((acc: { [id: string]: any }, param: TpETLComponentTypeConfigParamDef) => {
      acc[param.id] = param.paramType.defaultValue;
      return acc;
    }, {});
}

// Returns a new component, ready to be added to the pipeline
function createComp(pipeline: TpETLPipeline, compatibilityInfo: TpComptypeCompatibilityInfo): TpETLPipelineComponent {
  const compType = compatibilityInfo.compType;
  const inputObjectIds: { [sd: string]: string } = {};
  for (const conn of compatibilityInfo.inputConnections) {
    inputObjectIds[conn.inputId] = getETLComponentOutputObjectId(pipeline, conn.sourceCompId, conn.sourceOutputId);
  }
  const extraParamObjectIds = compatibilityInfo.extraParams.map((param) =>
    getETLComponentOutputObjectId(pipeline, param.sourceCompId, param.sourceOutputId),
  );
  const outputObjectIds: { [sd: string]: string } = {};
  for (const output of compType.outputs) outputObjectIds[output.id] = get_guid();
  return {
    id: get_guid(),
    name: compType.name,
    componentType: compType.id,
    inputObjectIds,
    extraParamObjectIds,
    outputObjectIds,
    params: defaultCompParams(compType.params),
  };
}

interface TpNewETLComponentFormProps {
  pipeline: TpETLPipeline;
  selectedOutputIds: string[];
  addComponent: (comp: TpETLPipelineComponent) => any;
  assetETLDataContext: TpETLDataContext | null;
  updatePipeline: (flow: TpETLFlowManifest) => any;
}

function getConnectionOutputString(pipeline: TpETLPipeline, conn: TpCompInputConnection): string {
  const sourceComp = getETLComponentById(pipeline, conn.sourceCompId);
  let inputName = sourceComp.name;
  if (Object.keys(sourceComp.outputObjectIds).length > 1) {
    // If the component has multiple outputs, we need to specify which one is used
    const outputInfo = getETLComponentTypeOutputInfo(
      getETLComponentType(sourceComp.componentType),
      conn.sourceOutputId,
    );
    inputName = `${outputInfo.name} from "${sourceComp.name}"`;
  }

  return inputName;
}

function NewETLComponentForm({
  pipeline,
  selectedOutputIds,
  addComponent,
  assetETLDataContext,
  updatePipeline,
}: TpNewETLComponentFormProps) {
  const tabTitles: Map<string, string> = new Map([
    ['flows', 'Templates'],
    ['components', 'Components'],
  ]);

  const selectedOutputs = selectedOutputIds.map((id) => getETLComponentWithOutputObj(pipeline, id));
  const compTypeCategories: any[] = [];

  for (const cat of allETLComponentCategories) {
    const catSection: any = {
      id: cat.id,
      name: cat.name,
      compTypes: [],
    };
    for (const compType of getAllETLComponentTypeManifests().filter((comp) => comp.categoryId == cat.id)) {
      const compatibilityInfo = getCompTypeCompatibilityInfo(selectedOutputs, compType);
      const dataAvailabilityInfo = getCompTypeAvailabilityInfo(compType, assetETLDataContext);
      if (compatibilityInfo.isCompatible && dataAvailabilityInfo.available) {
        catSection.compTypes.push(
          <div
            key={compType.id}
            className={styles.tileCompatibleCompType}
            onClick={() => addComponent(createComp(pipeline, compatibilityInfo))}
          >
            <div className={styles.tileIcon}>{compType.icon}</div>
            <div className={styles.tileTitle}>{compType.name}</div>
            <div className={styles.tileBody}>
              {compType.description}
              {compatibilityInfo.inputConnections.map((conn) => (
                <div key={conn.inputId} style={{ paddingTop: '4px' }}>
                  {getConnectionOutputString(pipeline, conn)} &rarr;
                  {getETLComponentTypeInputInfo(compType, conn.inputId).name}
                </div>
              ))}
            </div>
          </div>,
        );
      } else {
        const rejectionReasons = [...compatibilityInfo.rejectionReasons];
        if (dataAvailabilityInfo.message) rejectionReasons.push(dataAvailabilityInfo.message);
        catSection.compTypes.push(
          <div key={compType.id} className={styles.tileInCompatibleCompType}>
            <div>
              <div className={styles.tileIcon}>{compType.icon}</div>
              <div className={styles.tileTitle}>{compType.name}</div>
            </div>
            <div className={styles.tileBody}>
              {compType.description}
              <div className={styles.tileRejectionReason}>{rejectionReasons.join('; ')}</div>
            </div>
          </div>,
        );
      }
    }
    if (catSection.compTypes.length > 0) {
      compTypeCategories.push(catSection);
    }
  }

  const renderFlows = () => {
    return (
      <div key="flows">
        <p>These Templates are pre-configured pipelines that can be used to create a pipeline quickly.</p>
        <p>
          <b>Note: Selecting a Template will replace the current pipeline.</b>
        </p>
        {allETLFlows.map((flow) => (
          <div key={flow.id} className={styles.tileCompatibleCompType} onClick={() => updatePipeline(flow)}>
            <div>
              <div className={styles.tileIcon}>{flow.icon}</div>
              <div className={styles.tileTitle}>{flow.name}</div>
            </div>
            <div className={styles.tileBody}>{flow.description}</div>
          </div>
        ))}
      </div>
    );
  };

  const renderComponents = () => {
    return (
      <div key="components">
        <p>Click on a compatible component to add it to the pipeline.</p>
        <p>
          Select existing components in the pipeline with compatible outputs if you wish to add one of the components
          that are currently marked as not compatible.
        </p>
        {compTypeCategories.map((cat) => (
          <div key={cat.id}>
            <h2>{cat.name}</h2>
            {cat.compTypes}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.wrapper}>
      <h1>Add new component to pipeline</h1>

      <Tabber
        tabIds={[...tabTitles.keys()]}
        activeTabId={pipeline.components.length == 0 ? 'flows' : 'components'}
        getHeader={(tabId) => tabTitles.get(tabId) || tabId}
        renderContent={(tabId) => {
          if (tabId === 'flows') return renderFlows();
          else if (tabId === 'components') return renderComponents();
          return null;
        }}
        selectionStyle="bold"
      />
    </div>
  );
}

export default NewETLComponentForm;
